import React, { useState, useEffect } from 'react';
import {
  Box, Button,
} from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';

import { QuipUser, QuipUserWithInstitute } from '../../redux/types';
import { useAppDispatch } from '../../hooks/reduxHooks';
import ContentBox from '../../components/layout/ContentBox';
import ApiClient from '../../loopback/apiClient';
import UnverifiedUsers from '../../components/admin/UnverifiedUsers';
import DeclareReviewers from '../../components/admin/DeclareReviewers';
import ResetEmail from '../../components/admin/ResetEmail';
import MasterBookingList from '../../components/admin/MasterBookingList';
import downloadAsExcel from '../../components/utilities/ExcelExport';
import { setLoading, showErrorNotification, showNotification } from '../../redux/actions';

const UserManagerTab: React.FC = () => {
  const [quipUsers, setQuipUsers] = useState<QuipUserWithInstitute[]>();
  const [reviewers, setReviewers] = useState<QuipUser[]>();
  const navigateTo = useNavigate();
  const dispatch = useAppDispatch();

  const fetchUsersWithInstitute = async () => {
    const quipusers = await ApiClient.getUsersWithInstitute();
    setQuipUsers(quipusers);
  };

  const fetchReviewers = async () => {
    const fetchedReviewers = await ApiClient.getReviewers();
    setReviewers(fetchedReviewers);
  };

  useEffect(() => {
    dispatch(setLoading(true));
    fetchUsersWithInstitute()
      .then(fetchReviewers)
      .finally(() => dispatch(setLoading(false)));
  }, [dispatch]);

  const gotoUserManagement = () => navigateTo('/usermanagement');

  const resendVerification = (email: string) => {
    ApiClient.resendVerification(email)
      .then(() => {
        dispatch(showNotification('Success'));
      })
      .catch((e) => {
        dispatch(showErrorNotification(`Error during resend verification email: ${e.message}`));
      });
  };

  const deleteUser = (id: number) => ApiClient.deleteUser(id).then(fetchUsersWithInstitute);

  const declareReviewer = (user: QuipUser) => ApiClient.declareReviewer(user).then(fetchReviewers);

  const undeclareReviewer = (user: QuipUser) => ApiClient.undeclareReviewer(user)
    .then(fetchReviewers);

  const resetEmail = (user: QuipUser, newEmail: string) => ApiClient.resetUserEmail(user, newEmail)
    .then(fetchUsersWithInstitute);

  return (
    <>
      {
        quipUsers && reviewers && (
          <div>
            <ContentBox
              elevation={0}
              title={<FormattedMessage id="admindetails.generalActions" />}
            >
              <Box display="flex" gap={1}>
                <Button
                  variant="contained"
                  onClick={gotoUserManagement}
                  color="primary"
                >
                  <FormattedMessage id="admindetails.usersManagement" />
                </Button>
                <Button
                  variant="contained"
                  onClick={
                    () => ApiClient.exportUsers()
                      .then((data) => downloadAsExcel(data, `UserExport_${moment().locale('de').format('DDMMYYY')}`))
                  }
                  color="primary"
                >
                  <FormattedMessage id="admindetails.exportAllUsers" />
                </Button>
                <Button
                  variant="contained"
                  onClick={
                    () => ApiClient.exportInstitutes()
                      .then((data) => downloadAsExcel(data, `InstituteExport_${moment().locale('de').format('DDMMYYY')}`))
                  }
                  color="primary"
                >
                  <FormattedMessage id="admindetails.exportAllInstitutes" />
                </Button>
              </Box>
            </ContentBox>
            <ContentBox
              elevation={0}
              title={<FormattedMessage id="admindetails.unverifiedUsers" />}
            >
              <UnverifiedUsers
                users={
                  quipUsers.filter(
                    (user) => (!user.emailVerified),
                  )
                }
                resendVerification={resendVerification}
                deleteUser={deleteUser}
              />
            </ContentBox>
            <ContentBox
              elevation={0}
              title={<FormattedMessage id="admindetails.declareReviewers" />}
            >
              {
                reviewers
                && (
                <DeclareReviewers
                  users={quipUsers}
                  reviewers={reviewers}
                  onDeclareReviewer={declareReviewer}
                  onUndeclareReviewer={undeclareReviewer}
                />
                )
              }
            </ContentBox>
            <ContentBox
              elevation={0}
              title={<FormattedMessage id="admindetails.resetEmailAdress" />}
            >
              <ResetEmail
                users={quipUsers}
                onResetEmail={resetEmail}
              />
            </ContentBox>
            <ContentBox
              elevation={0}
              title={<FormattedMessage id="admindetails.geocodeUnlocatedInstitutesHeadline" />}
              show={false}
            >
              <Button
                variant="contained"
                className="mr3"
                onClick={ApiClient.geocodeUnlocatedInstitutes}
                color="primary"
              >
                <FormattedMessage id="admindetails.geocodeUnlocatedInstitutesButton" />
              </Button>
            </ContentBox>
            <ContentBox
              elevation={0}
              title={<FormattedMessage id="admindetails.masterBookingListHeadline" />}
            >
              <MasterBookingList
                createMasterBookingList={ApiClient.createMasterBookingList}
              />
            </ContentBox>
          </div>
        )
      }
    </>
  );
};

export default UserManagerTab;
